import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import type { Tagged } from "@-/com.npmjs.type-fest";
import useLatest from "@-/com.npmjs.use-latest";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import {
  type HanCoinDepositSourceName,
  useHanCoinDeposit,
} from "@-/dev.improve.haniverse.components.ui.han-coin-deposit";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import {
  type ClaimedDailyLoginReward,
  type DailyLoginRewardDay,
  type HanReward,
  useClaimDailyLoginRewardMutation,
  useDailyLoginRewardQueryOptions,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { useCallback, useRef, useState } from "react";

/**
 * Proof that the Welcome Back card shows today's 红包: unopened, or opened from this card. Either
 * way `cycleDay` is today's 1-based reward within this calendar week; `week` includes empty days.
 */
export type LearnSummaryDailyRewardGate = Tagged<
  { cycleDay: number; kind: "collected" | "offer"; reward: HanReward; week: DailyLoginRewardDay[] },
  "LearnSummaryDailyRewardGate"
>;

export type LearnSummaryDailyRewardState = {
  /** Opens today's 红包 if it is still unclaimed; a no-op once it is. Safe to call repeatedly. */
  claim: () => void;
  /** `null` = the designed hidden state: already opened today, or not loaded yet. */
  gate: LearnSummaryDailyRewardGate | null;
};

/**
 * Today's 红包 on the Welcome Back card. Only Continue Learning opens it: its coins burst
 * out of `source` (the button) into the drawer
 * button. The claim is idempotent per local day, so calling `claim` again (or on another device)
 * never pays twice. The strip stays on the card, today ticked off, so the grid never jumps.
 */
export function useLearnSummaryDailyReward({
  enabled,
  source = "daily-reward",
}: {
  enabled: boolean;
  source?: HanCoinDepositSourceName;
}): LearnSummaryDailyRewardState {
  const { data } = useQuery({
    ...useDailyLoginRewardQueryOptions(),
    enabled,
    throwOnError: false,
  });
  const claimMutation = useClaimDailyLoginRewardMutation();
  const claimMutationRef: { readonly current: typeof claimMutation } = useLatest(claimMutation);
  const deposit = useHanCoinDeposit();
  const [claimed, setClaimed] = useState<ClaimedDailyLoginReward | null>(null);
  // One claim at a time, and none after this card has opened today's envelope.
  const isClaimed = useRef(false);
  const isClaiming = useRef(false);
  const claim = useCallback(() => {
    if (!enabled || isClaimed.current || isClaiming.current) {
      return;
    }
    isClaiming.current = true;
    void claimMutationRef.current
      .mutateAsync()
      .then((result) => {
        isClaimed.current = true;
        if (result !== null) {
          setClaimed(result);
          deposit({
            dragons: result.dragons,
            han: result.han,
            hanMiles: result.hanMiles,
            reviewBonusHan: result.reviewBonusHan,
            source,
          });
        }
      })
      .catch((error: unknown) => {
        showErrorToast("Could not open today's 红包", error);
      })
      .finally(() => {
        isClaiming.current = false;
      });
  }, [claimMutationRef, deposit, enabled, source]);
  let gate: LearnSummaryDailyRewardGate | null = null;
  if (claimed !== null) {
    gate = createGate<LearnSummaryDailyRewardGate>({
      cycleDay: claimed.cycleDay,
      kind: "collected",
      reward: claimed,
      week: claimed.week,
    });
  } else if (enabled && data?.claimable === true) {
    gate = createGate<LearnSummaryDailyRewardGate>({
      cycleDay: data.nextCycleDay,
      kind: "offer",
      reward: data.next,
      week: data.week,
    });
  }
  return { claim, gate };
}
